/**
 * Slice 11 — Reconnect helper for `ConnectionDiagnosticsDialog`.
 *
 * When the listing failed with `listingErrorReason: 'no_pool'`, the user
 * clicks the dialog's *Reconnect* button. This helper wraps the existing
 * `connect-to-database` flow (no new IPC), invalidates the cached
 * diagnostics report so the next open re-fetches, and returns a boolean for
 * the caller to act on.
 *
 * Note: when the renderer-side connection has a stored password
 * (`hasStoredPassword === true` with empty `password`), we fetch the
 * decrypted password from the keychain before invoking the IPC — same
 * pattern as `connectionStore.connectToDatabase`. Sending the renderer's
 * masked connection straight to the IPC would always fail with "Login
 * failed".
 *
 * Kept in its own module so the parent dialog file stays under the 200-line
 * guideline and the logic can be unit-tested without React.
 */

import type { Connection } from '../../../types/electron';
import { invalidateDiagnosticsCacheForConnection } from '../diagnosticsReportCache';

export type ReconnectOutcome = { success: boolean; error?: string };

export async function reconnectAndInvalidate(
  connection: Connection
): Promise<ReconnectOutcome> {
  if (typeof window === 'undefined' || !window.electronAPI?.connectToDatabase) {
    return { success: false, error: 'Reconnect unavailable in this context' };
  }
  try {
    let connectionWithPassword: Connection = connection;
    if (
      connection.hasStoredPassword &&
      !connection.password &&
      window.electronAPI?.getDecryptedPassword
    ) {
      try {
        const decrypted = await window.electronAPI.getDecryptedPassword(connection.id);
        if (typeof decrypted === 'string' && decrypted.length > 0) {
          connectionWithPassword = { ...connection, password: decrypted };
        }
      } catch {
        // Fall back to the original (likely masked) connection — IPC will
        // surface the real "Login failed" error in `result.error`.
      }
    }

    const result = await window.electronAPI.connectToDatabase(connectionWithPassword);
    if (result?.success) {
      invalidateDiagnosticsCacheForConnection(connection.id);
      return { success: true };
    }
    return { success: false, error: result?.error };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Reconnect failed';
    return { success: false, error: message };
  }
}
